const API_BASE = '/api/v1/submissions';

export interface Submission {
  id: string;
  company_id: string;
  candidate_id: string;
  client_company_id: string | null;
  client_contact_id: string | null;
  job_posting_id: string | null;
  status: SubmissionStatus;
  submitted_at: string | null;
  client_feedback: string | null;
  client_replied_at: string | null;
  interview_id: string | null;
  interview_date: string | null;
  offered_salary: string | null;
  offer_date: string | null;
  outcome_notes: string | null;
  rejected_reason: string | null;
  submitted_by: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  candidates?: {
    id: string;
    first_name: string;
    last_name: string;
    current_position: string;
    city: string;
    stage: string;
    email: string;
    phone: string;
    salary_expectation?: string;
  };
  client_company?: {
    id: string;
    name: string;
    industry: string;
    phone: string;
    website?: string;
  };
  client_contact?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    title?: string;
  };
}

export type SubmissionStatus =
  | 'draft'
  | 'sent'
  | 'client_review'
  | 'interview_scheduled'
  | 'offer'
  | 'accepted'
  | 'rejected'
  | 'withdrawn';

export const SUBMISSION_STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Entwurf', color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.1)' },
  sent: { label: 'Gesendet', color: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.1)' },
  client_review: { label: 'Beim Kunden', color: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.1)' },
  interview_scheduled: { label: 'Interview geplant', color: '#7C3AED', bgColor: 'rgba(124, 58, 237, 0.1)' },
  offer: { label: 'Angebot', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' },
  accepted: { label: 'Angenommen', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' },
  rejected: { label: 'Abgelehnt', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
  withdrawn: { label: 'Zurueckgezogen', color: '#9CA3AF', bgColor: 'rgba(156, 163, 175, 0.1)' },
};

export interface CreateSubmissionParams {
  candidate_id: string;
  client_company_id?: string;
  client_contact_id?: string;
  job_posting_id?: string;
  auto_send?: boolean;
}

export interface ListSubmissionsParams {
  candidate_id?: string;
  client_company_id?: string;
  status?: SubmissionStatus;
  assigned_to?: string;
  limit?: number;
  offset?: number;
}

async function fetchApi(url: string, options: RequestInit = {}) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || 'Request failed');
  }
  return json.data;
}

const submissionsService = {
  async list(params: ListSubmissionsParams = {}): Promise<Submission[]> {
    const searchParams = new URLSearchParams();
    if (params.candidate_id) searchParams.set('candidate_id', params.candidate_id);
    if (params.client_company_id) searchParams.set('client_company_id', params.client_company_id);
    if (params.status) searchParams.set('status', params.status);
    if (params.assigned_to) searchParams.set('assigned_to', params.assigned_to);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const qs = searchParams.toString();
    return fetchApi(`${API_BASE}${qs ? `?${qs}` : ''}`);
  },

  async get(id: string): Promise<Submission> {
    return fetchApi(`${API_BASE}/${id}`);
  },

  async create(params: CreateSubmissionParams): Promise<Submission> {
    return fetchApi(API_BASE, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  async update(id: string, updates: Partial<Submission>): Promise<Submission> {
    return fetchApi(`${API_BASE}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async send(id: string): Promise<Submission> {
    return fetchApi(`${API_BASE}/${id}/send`, {
      method: 'PATCH',
    });
  },

  async recordClientReply(id: string, feedback: string, positive: boolean): Promise<Submission> {
    return fetchApi(`${API_BASE}/${id}/client-reply`, {
      method: 'PATCH',
      body: JSON.stringify({ feedback, positive }),
    });
  },

  /**
   * Statuswechsel — der einzige Weg, den Status eines Vorschlags zu aendern.
   * Das Backend prueft den Uebergang und schreibt den Verlauf.
   */
  async changeStatus(id: string, to: SubmissionStatus, note?: string, rejectedReason?: string): Promise<Submission> {
    return fetchApi(`${API_BASE}/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ to, note, rejected_reason: rejectedReason }),
    });
  },

  async remove(id: string): Promise<void> {
    return fetchApi(`${API_BASE}/${id}`, {
      method: 'DELETE',
    });
  },
};

export default submissionsService;
